import { DateTime } from "luxon"
import type { LocationQueryRaw } from "vue-router"
import { BenefitTypeEnum } from "@vitable/asclepius-api-client-ts"
import { VPC_STEP } from "@common/utilities/benefit-utils"
import { getEmployerAllowanceCents, isPlanShoppingResolved } from "@/modules/enrollment-new/enrollment-flow-helpers"
import {
  planShoppingEditRoute,
  planShoppingEntryRoute,
  type EnrollmentRouteLocation
} from "@/modules/enrollment-new/enrollment-routes"
import {
  ENROLLMENT_STEP_ROUTES,
  isEnrollRouteName,
  isRoutableBenefitStep,
  PLAN_SHOPPING_STEP,
  type EnrollmentBenefitStep,
  type EnrollmentStepKey,
  type EnrollRouteName,
  type NonVpcBenefitElection,
  type NonVpcElectionsWithYear,
  type RoutableEnrollmentBenefitStep,
  type VpcBenefitElection
} from "@/modules/enrollment-new/enrollment-flow-types"

export type { EnrollmentRouteLocation }

export type SegmentKind = "fixed" | "benefit" | "plan-shopping"

/** True for any segment that represents user-actionable progress (not a fixed bookend). */
export function isStepSegment(seg: EnrollmentSegment): boolean {
  return seg.kind !== "fixed"
}

export interface EnrollmentSegment {
  /** Stable identifier — route name for fixed segments, "<type>:<year>" for benefit-with-year, "vpc" for VPC. */
  key: string
  route: EnrollmentRouteLocation
  kind: SegmentKind
  /** Stepper-bar group; adjacent segments sharing a key collapse into one bar entry. */
  barGroupKey: EnrollmentStepKey
  barTitle: string
  /** True iff this segment has no outstanding action blocking forward navigation. */
  isComplete: boolean
  /** Coverage year for benefit/plan-shopping segments; absent for VPC and fixed bookends. */
  year?: number
}

export interface BuildSegmentsInput {
  availableBenefitTypes: ReadonlyArray<EnrollmentBenefitStep>
  electionsByType: Readonly<Record<string, ReadonlyArray<NonVpcBenefitElection>>>
  vpcElection: VpcBenefitElection | undefined
  query: LocationQueryRaw
  medicareEligibleIn: boolean
}

const FIXED_BAR_TITLES = {
  background: "Background",
  dependents: "Dependents",
  "sign-forms": "Sign Forms"
} as const

const BENEFIT_BAR_TITLES: Record<EnrollmentBenefitStep, string> = {
  [BenefitTypeEnum.Medical]: "Medical",
  [BenefitTypeEnum.Dental]: "Dental",
  [BenefitTypeEnum.Vision]: "Vision",
  [BenefitTypeEnum.Hospital]: "Hospital",
  [VPC_STEP]: "Vitable Primary Care"
}

function planShoppingBarTitle(isMedicareEligible: boolean): string {
  return isMedicareEligible ? "Medicare" : "Plan Shopping"
}

export function getElectionYear(election: NonVpcBenefitElection): number | undefined {
  const start = election.enrollment.plan_year_coverage_start
  if (!start) return undefined
  const parsed = DateTime.fromISO(start)
  return parsed.isValid ? parsed.year : undefined
}

export function groupElectionsByYear(elections: ReadonlyArray<NonVpcBenefitElection>): Array<NonVpcElectionsWithYear> {
  const byYear = new Map<number, NonVpcBenefitElection[]>()
  for (const election of elections) {
    const year = getElectionYear(election)
    if (year === undefined) continue
    const bucket = byYear.get(year) ?? []
    bucket.push(election)
    byYear.set(year, bucket)
  }
  return [...byYear.entries()].sort(([a], [b]) => a - b).map(([year, group]) => ({ year, elections: group }))
}

function buildBenefitYearSegment(
  step: RoutableEnrollmentBenefitStep,
  year: number,
  elections: ReadonlyArray<NonVpcBenefitElection>,
  routeName: EnrollRouteName,
  query: LocationQueryRaw
): EnrollmentSegment {
  return {
    key: `${step}:${year}`,
    route: { name: routeName, params: { year: String(year) }, query },
    kind: "benefit",
    barGroupKey: step,
    barTitle: BENEFIT_BAR_TITLES[step],
    isComplete: elections.every((e) => e.selection !== undefined),
    year
  }
}

function buildVpcSegment(query: LocationQueryRaw): EnrollmentSegment {
  return {
    key: VPC_STEP,
    route: { name: ENROLLMENT_STEP_ROUTES[VPC_STEP], query },
    kind: "benefit",
    barGroupKey: VPC_STEP,
    barTitle: BENEFIT_BAR_TITLES[VPC_STEP],
    isComplete: true
  }
}

function buildPlanShoppingSegment(
  year: number,
  ichraElections: ReadonlyArray<NonVpcBenefitElection>,
  medicareEligibleIn: boolean,
  query: LocationQueryRaw
): EnrollmentSegment {
  const ichra = ichraElections[0]
  return {
    key: `${PLAN_SHOPPING_STEP}:${year}`,
    route: planShoppingEntryRoute({
      year,
      medicareEligibleIn,
      enrollmentId: ichra.enrollment.id,
      employerAllowanceInCents: getEmployerAllowanceCents(ichra.selection),
      query
    }),
    kind: "plan-shopping",
    barGroupKey: PLAN_SHOPPING_STEP,
    barTitle: planShoppingBarTitle(medicareEligibleIn),
    isComplete: ichraElections.every(isPlanShoppingResolved),
    year
  }
}

function buildFixedSegment(
  key: "background" | "dependents" | "sign-forms",
  query: LocationQueryRaw
): EnrollmentSegment {
  return {
    key,
    route: { name: ENROLLMENT_STEP_ROUTES[key], query },
    kind: "fixed",
    barGroupKey: key,
    barTitle: FIXED_BAR_TITLES[key],
    isComplete: true
  }
}

/**
 * Pure ordering algorithm. Reads top-to-bottom: bookend, benefit types in canonical order,
 * one plan-shopping segment per ICHRA medical year, bookend. The plan-shopping leg (primer +
 * browse, or the Medicare-call page for 65+) sits at the end of the flow because it's a
 * fundamentally different experience (external marketplace) and shouldn't break the rhythm
 * of the other benefits.
 */
export function buildEnrollmentSegments(input: BuildSegmentsInput): EnrollmentSegment[] {
  const { availableBenefitTypes, electionsByType, vpcElection, query, medicareEligibleIn } = input
  const segments: EnrollmentSegment[] = []

  segments.push(buildFixedSegment("background", query))
  segments.push(buildFixedSegment("dependents", query))

  for (const step of availableBenefitTypes) {
    if (step === VPC_STEP) {
      if (!vpcElection) continue
      segments.push(buildVpcSegment(query))
      continue
    }

    if (!isRoutableBenefitStep(step)) continue // defensive: skip Hospital and any future un-routed benefit types

    const routeName = ENROLLMENT_STEP_ROUTES[step]
    const elections = electionsByType[step] ?? []
    for (const yearGroup of groupElectionsByYear(elections)) {
      segments.push(buildBenefitYearSegment(step, yearGroup.year, yearGroup.elections, routeName, query))
    }
  }

  const medicalElections = electionsByType[BenefitTypeEnum.Medical] ?? []
  for (const yearGroup of groupElectionsByYear(medicalElections)) {
    const ichraElections = yearGroup.elections.filter((e) => e.enrollment.requires_external_plan_in)
    if (ichraElections.length === 0) continue
    if (ichraElections.every((e) => e.selection === null)) continue
    segments.push(buildPlanShoppingSegment(yearGroup.year, ichraElections, medicareEligibleIn, query))
  }

  segments.push(buildFixedSegment("sign-forms", query))

  return segments
}

export function findCurrentSegmentIndex(
  segments: ReadonlyArray<EnrollmentSegment>,
  current: {
    name?: EnrollRouteName | string | symbol | null | undefined
    params?: { year?: string | string[] }
    meta?: { parentStepKey?: EnrollmentStepKey }
  }
): number {
  const name = isEnrollRouteName(current.name) ? current.name : undefined
  if (!name) return 0

  const parentKey = current.meta?.parentStepKey
  const yearRaw = Array.isArray(current.params?.year) ? current.params?.year[0] : current.params?.year
  const year = yearRaw !== undefined ? Number(yearRaw) : undefined

  const idx = segments.findIndex((s) => {
    const matchesLocation = parentKey ? isStepSegment(s) && s.barGroupKey === parentKey : s.route.name === name
    if (!matchesLocation) return false
    return year === undefined || s.year === year
  })
  if (idx !== -1) return idx

  const fallback = segments.findIndex((s) => s.route.name === name)
  return fallback === -1 ? 0 : fallback
}

/**
 * Collapse adjacent or repeated same-bar-group segments into one bar entry per group,
 * preserving order of first appearance. Multi-year medical → one "Medical" bar entry
 * routed at the first year.
 */
export interface BarEntry {
  barGroupKey: EnrollmentStepKey
  barTitle: string
  route: EnrollmentRouteLocation
  /** Index into the underlying segments array — used for completeness checks. */
  firstSegmentIndex: number
}

export function collapseToBar(segments: ReadonlyArray<EnrollmentSegment>): BarEntry[] {
  const seen = new Set<EnrollmentStepKey>()
  const bar: BarEntry[] = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (seen.has(seg.barGroupKey)) continue
    seen.add(seg.barGroupKey)
    bar.push({
      barGroupKey: seg.barGroupKey,
      barTitle: seg.barTitle,
      route: seg.route,
      firstSegmentIndex: i
    })
  }
  return bar
}

/**
 * Are all benefit (and plan-shopping) segments strictly before `targetSegmentIndex` complete?
 * Mirrors the legacy `ensurePriorBenefitTypesComplete` invariant — VPC skipped, fixed bookends
 * skipped. Returns the route of the first incomplete segment, or `true` when clear.
 */
export function priorBenefitsCompleteOrRedirect(
  segments: ReadonlyArray<EnrollmentSegment>,
  targetSegmentIndex: number
): true | EnrollmentRouteLocation {
  for (let i = 0; i < targetSegmentIndex; i++) {
    const seg = segments[i]
    if (!isStepSegment(seg)) continue
    if (seg.barGroupKey === VPC_STEP) continue
    if (!seg.isComplete) return seg.route
  }
  return true
}

export function findBenefitStepEditRoute(
  segments: ReadonlyArray<EnrollmentSegment>,
  step: EnrollmentBenefitStep,
  year: number | undefined,
  election: NonVpcBenefitElection
): EnrollmentRouteLocation | undefined {
  if (step === BenefitTypeEnum.Medical && year !== undefined) {
    const route = planShoppingEditRoute({ year, selection: election.selection, enrollmentId: election.enrollment.id })
    if (route) return route
  }
  return segments.find((s) => s.kind === "benefit" && s.barGroupKey === step && s.year === year)?.route
}
